import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Grid, TextField,
  FormControlLabel, Checkbox, Typography, Chip, Box, Divider, useTheme, alpha
} from '@mui/material';
import { supabase } from '../../../../supabaseClient';

const QATestStep = forwardRef(({ currentStep, formulation }, ref) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    test_type: '',
    test_method: '',
    target_result: '',
    acceptance_range: '',
    number_of_samples: '',
    allowed_failures: '',
    confidence_level: '',
    sampling_method: '',
    equipment_name: '',
    calibration_schedule: '',
    environment_requirements: '',
    qualified_personnel: '',
    detection_threshold: '',
    quantification_threshold: '',
    upper_warning_limit: '',
    lower_warning_limit: '',
    failure_response: '',
    failure_notification: '',
    critical_test: false,
    release_test: false,
    trend_tracking: false,
    statistical_control: false
  });

  // Load existing QA test details when component mounts
  useEffect(() => {
    if (currentStep?.qa_test_details && Object.keys(currentStep.qa_test_details).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...currentStep.qa_test_details
      }));
      }
  }, [currentStep?.id]);

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    saveData: async () => {
      if (!currentStep?.id) return false;

    try {
      const { error } = await supabase
        .from('procedure_steps')
        .update({
            qa_test_details: formData,
            updated_at: new Date().toISOString()
        })
        .eq('id', currentStep.id);

      if (error) throw error;
        return true;
    } catch (error) {
      console.error('Error saving QA test data:', error);
        return false;
    }
    },
    formData: formData
  }), [formData, currentStep?.id]);

  // All field handlers - ONLY update local state, no database saves
  const handleTestTypeChange = (e) => {
    setFormData(prev => ({...prev, test_type: e.target.value}));
  };

  const handleTestMethodChange = (e) => {
    setFormData(prev => ({...prev, test_method: e.target.value}));
  };

  const handleTargetResultChange = (e) => {
    setFormData(prev => ({...prev, target_result: e.target.value}));
  };

  const handleAcceptanceRangeChange = (e) => {
    setFormData(prev => ({...prev, acceptance_range: e.target.value}));
  };

  const handleNumberOfSamplesChange = (e) => {
    setFormData(prev => ({...prev, number_of_samples: e.target.value}));
  };

  const handleAllowedFailuresChange = (e) => {
    setFormData(prev => ({...prev, allowed_failures: e.target.value}));
  };

  const handleConfidenceLevelChange = (e) => {
    setFormData(prev => ({...prev, confidence_level: e.target.value}));
  };

  const handleSamplingMethodChange = (e) => {
    setFormData(prev => ({...prev, sampling_method: e.target.value}));
  };

  const handleEquipmentNameChange = (e) => {
    setFormData(prev => ({...prev, equipment_name: e.target.value}));
  };

  const handleCalibrationScheduleChange = (e) => {
    setFormData(prev => ({...prev, calibration_schedule: e.target.value}));
  };

  const handleEnvironmentRequirementsChange = (e) => {
    setFormData(prev => ({...prev, environment_requirements: e.target.value}));
  };

  const handleQualifiedPersonnelChange = (e) => {
    setFormData(prev => ({...prev, qualified_personnel: e.target.value}));
  };

  const handleDetectionThresholdChange = (e) => {
    setFormData(prev => ({...prev, detection_threshold: e.target.value}));
  };

  const handleQuantificationThresholdChange = (e) => {
    setFormData(prev => ({...prev, quantification_threshold: e.target.value}));
  };

  const handleUpperWarningLimitChange = (e) => {
    setFormData(prev => ({...prev, upper_warning_limit: e.target.value}));
  };

  const handleLowerWarningLimitChange = (e) => {
    setFormData(prev => ({...prev, lower_warning_limit: e.target.value}));
  };

  const handleFailureResponseChange = (e) => {
    setFormData(prev => ({...prev, failure_response: e.target.value}));
  };

  const handleFailureNotificationChange = (e) => {
    setFormData(prev => ({...prev, failure_notification: e.target.value}));
  };

  const handleCriticalTestChange = (e) => {
    setFormData(prev => ({...prev, critical_test: e.target.checked}));
  };

  const handleReleaseTestChange = (e) => {
    setFormData(prev => ({...prev, release_test: e.target.checked}));
  };

  const handleTrendTrackingChange = (e) => {
    setFormData(prev => ({...prev, trend_tracking: e.target.checked}));
  };

  const handleStatisticalControlChange = (e) => {
    setFormData(prev => ({...prev, statistical_control: e.target.checked}));
  };

  return (
    <Box sx={{ 
      p: 4,
      background: `linear-gradient(135deg, ${alpha('#f8f9fa', 0.8)}, ${alpha('#e9ecef', 0.6)})`,
      backdropFilter: 'blur(10px)',
      borderRadius: 4,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
    }}>
      <Box sx={{ 
        mb: 4,
        p: 4,
        borderRadius: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 500, 
          color: theme.palette.primary.main, 
          mb: 1,
          textAlign: 'center'
        }}>
          QA Test Point Specifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 500 }}>
          Define comprehensive quality control testing procedures and specifications
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Test Configuration Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.info.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.info.main, 
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              Test Configuration
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="What are you testing?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.test_type}
                  onChange={handleTestTypeChange}
                  placeholder="pH, moisture content, particle size, purity, potency"
                  size="small"
                  helperText="Describe what property or quality you're measuring"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How will you test it?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.test_method}
                  onChange={handleTestMethodChange}
                  placeholder="pH meter, HPLC, visual inspection, titration"
                  size="small"
                  helperText="What equipment or method will you use"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="What result do you expect?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.target_result}
                  onChange={handleTargetResultChange}
                  placeholder="7.0 pH, Clear solution, 95-105% potency"
                  size="small"
                  helperText="Describe the ideal result you want to see"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="What's the acceptable range?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.acceptance_range}
                  onChange={handleAcceptanceRangeChange}
                  placeholder="6.5-7.5 pH, No particles, 90-110% potency"
                  size="small"
                  helperText="The range where results are still acceptable"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Sampling Strategy Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.warning.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.warning.main, 
              mb: 3
            }}>
              Sampling Strategy
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="How many samples to test?"
                  type="number"
                  fullWidth
                  value={formData.number_of_samples}
                  onChange={handleNumberOfSamplesChange}
                  placeholder="5"
                  size="small"
                  helperText="Usually 3-10 samples"
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="How many failures allowed?"
                  type="number"
                  fullWidth
                  value={formData.allowed_failures}
                  onChange={handleAllowedFailuresChange}
                  placeholder="0"
                  size="small"
                  helperText="Usually 0 for critical tests"
                  inputProps={{ min: 0 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Confidence Level"
                  fullWidth
                  value={formData.confidence_level}
                  onChange={handleConfidenceLevelChange}
                  placeholder="90% lower confidence; 95% standard confidence; 99% high confidence"
                  size="small"
                  helperText="Statistical confidence in your test results"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How to pick samples?"
                  fullWidth
                  value={formData.sampling_method}
                  onChange={handleSamplingMethodChange}
                  placeholder="Random samples from beginning, middle, end of batch"
                  size="small"
                  helperText="Describe your sampling strategy"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Sampling Plan Summary:
                  </Typography>
                  <Box sx={{ 
                    p: 3, 
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.1)}`
                  }}>
                    <Typography variant="body2" sx={{ color: theme.palette.success.dark, fontWeight: 500 }}>
                      Test {formData.number_of_samples || 'N'} samples, allow {formData.allowed_failures || '0'} failures
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.critical_test}
                      onChange={handleCriticalTestChange}
                      sx={{
                        color: theme.palette.warning.main,
                        '&.Mui-checked': {
                          color: theme.palette.warning.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      This is a Critical Test (cannot fail)
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.release_test}
                      onChange={handleReleaseTestChange}
                      sx={{
                        color: theme.palette.warning.main,
                        '&.Mui-checked': {
                          color: theme.palette.warning.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Required before product release
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Equipment & Environment Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Equipment & Environment
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Equipment/Instrument to use"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.equipment_name}
                  onChange={handleEquipmentNameChange}
                  placeholder="Lab Balance #1, HPLC System A, pH Meter #3"
                  size="small"
                  helperText="Specific equipment ID or name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How often to calibrate?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.calibration_schedule}
                  onChange={handleCalibrationScheduleChange}
                  placeholder="Before each use for most accurate; Daily for standard practice; Weekly for stable equipment"
                  size="small"
                  helperText="Calibration frequency requirements"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Room/Environment requirements"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.environment_requirements}
                  onChange={handleEnvironmentRequirementsChange}
                  placeholder="Normal lab conditions; Climate controlled temp/humidity; Clean room sterile environment; Fume hood for safety"
                  size="small"
                  helperText="Environmental conditions needed"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Who can perform this test?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.qualified_personnel}
                  onChange={handleQualifiedPersonnelChange}
                  placeholder="QC Technician Level II, Trained Analyst"
                  size="small"
                  helperText="Required qualification level"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Detection Limits Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Test Limits & Sensitivity
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Smallest detectable amount"
                  fullWidth
                  value={formData.detection_threshold}
                  onChange={handleDetectionThresholdChange}
                  placeholder="0.05 mg/L, 0.1%, 1 ppm"
                  size="small"
                  helperText="Smallest amount your equipment can detect"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Smallest measurable amount"
                  fullWidth
                  value={formData.quantification_threshold}
                  onChange={handleQuantificationThresholdChange}
                  placeholder="0.15 mg/L, 0.3%, 3 ppm"
                  size="small"
                  helperText="Smallest amount you can measure accurately"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Upper warning level"
                  fullWidth
                  value={formData.upper_warning_limit}
                  onChange={handleUpperWarningLimitChange}
                  placeholder="103% of target, 7.2 pH, 1200 cP"
                  size="small"
                  helperText="When results get this high, investigate but don't necessarily fail"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Lower warning level"
                  fullWidth
                  value={formData.lower_warning_limit}
                  onChange={handleLowerWarningLimitChange}
                  placeholder="97% of target, 6.8 pH, 800 cP"
                  size="small"
                  helperText="When results get this low, investigate but don't necessarily fail"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Documentation & Response Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.error.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.error.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.error.main, 
              mb: 3
            }}>
              Documentation & Response Plan
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="What to do if test fails?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.failure_response}
                  onChange={handleFailureResponseChange}
                  placeholder="1. Retest immediately, 2. Check equipment calibration, 3. Investigate root cause, 4. Document everything"
                  size="small"
                  helperText="Step-by-step response plan"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.error.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Who to notify if test fails?"
                  fullWidth
                  value={formData.failure_notification}
                  onChange={handleFailureNotificationChange}
                  placeholder="QA Manager, Production Supervisor, R&D Lead"
                  size="small"
                  helperText="People who need to know immediately"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.error.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.trend_tracking}
                      onChange={handleTrendTrackingChange}
                      sx={{
                        color: theme.palette.error.main,
                        '&.Mui-checked': {
                          color: theme.palette.error.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Track results over time for trends
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.statistical_control}
                      onChange={handleStatisticalControlChange}
                      sx={{
                        color: theme.palette.error.main,
                        '&.Mui-checked': {
                          color: theme.palette.error.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Use statistical process control (SPC) charts
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});

QATestStep.displayName = 'QATestStep';

export default QATestStep; 